import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import {
  MapPin, Zap, CheckCircle2, Sun, Moon, ArrowRight, X,
  Search, Star, Phone, Globe, ChevronDown, Users, Shield,
  BarChart2, Download, Target, Database, Award, Clock
} from 'lucide-react';
import api from '../utils/api';

const PLAN_COLOR = {
  free:'#6b7280', individual:'#3b82f6', pro:'#e11d48', pro_plus:'#8b5cf6', enterprise:'#f59e0b'
};

const QUICK_SEARCHES = [
  { label:'🍽️ Restaurants in Mumbai',   q:'restaurants',    loc:'Mumbai' },
  { label:'🏭 Factories in Pune',        q:'factory',        loc:'Pune' },
  { label:'💻 IT Companies Bangalore',   q:'IT company',     loc:'Bangalore' },
  { label:'🏥 Hospitals in Delhi',       q:'hospital',       loc:'Delhi' },
  { label:'🧵 Textile in Surat',         q:'textile',        loc:'Surat' },
  { label:'🏨 Hotels in Goa',            q:'hotel',          loc:'Goa' },
];

const FEATURES = [
  { icon:'📍', title:'Location Search',    desc:'Any city, area, landmark — results in 2 seconds' },
  { icon:'🎯', title:'80+ Categories',     desc:'Every business type from restaurants to factories' },
  { icon:'📊', title:'Lead Scoring',       desc:'0–100 score by phone, rating, website, reviews' },
  { icon:'📧', title:'Email Campaigns',    desc:'Send bulk emails directly to leads from the app' },
  { icon:'💬', title:'WhatsApp Outreach',  desc:'Bulk WhatsApp messages to leads with phone numbers' },
  { icon:'👥', title:'Team Management',    desc:'Invite team, assign leads, track progress together' },
];

// ── PurchaseFlow ───────────────────────────────────────────────────────────
function PurchaseFlow({ plan, onClose }) {
  const [billing,setBilling] = useState('monthly');
  const [step,setStep]       = useState('confirm');
  const [txnId,setTxnId]     = useState('');
  const [subId,setSubId]     = useState('');
  const [payInfo,setPayInfo] = useState(null);
  const [loading,setLoading] = useState(false);
  const navigate = useNavigate();
  const monthly = plan.price_monthly||0;
  const annual  = plan.price_annual ? plan.price_annual*12 : monthly*12;
  const amount  = billing==='annual' ? annual : monthly;
  const color   = PLAN_COLOR[plan.code]||'#e11d48';
  const feats   = typeof plan.features==='string' ? JSON.parse(plan.features||'[]') : plan.features||[];

  const initiate = async () => {
    setLoading(true);
    try {
      const r = await api.post('/subscription/initiate',{plan:plan.code,billing});
      if (r.data.gateway==='phonepe'&&r.data.paymentUrl) {
        window.open(r.data.paymentUrl,'_blank'); setSubId(r.data.subscription.id); setStep('payment'); setPayInfo({...r.data.payment,isPhonePe:true});
      } else { setSubId(r.data.subscription.id); setPayInfo(r.data.payment); setStep('payment'); }
    } catch(err) { alert(err.response?.data?.message||'Failed'); }
    finally { setLoading(false); }
  };
  const confirm = async () => {
    if (!txnId.trim()) { alert('Enter transaction ID'); return; }
    setLoading(true);
    try { await api.post('/subscription/confirm',{subscriptionId:subId,transactionId:txnId.trim()}); setStep('done'); }
    catch(err) { alert(err.response?.data?.message||'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',zIndex:999,display:'flex',alignItems:'center',justifyContent:'center',padding:20}} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:'var(--surface)',borderRadius:16,width:'100%',maxWidth:480,maxHeight:'90vh',overflowY:'auto',boxShadow:'0 24px 64px rgba(0,0,0,.3)'}}>
        <div style={{padding:'20px 24px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <div style={{fontFamily:'var(--font-display)',fontWeight:800,fontSize:18}}>{step==='done'?'🎉 Payment Submitted':`Upgrade to ${plan.name}`}</div>
          <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',padding:4,color:'var(--text3)'}}><X size={18}/></button>
        </div>
        <div style={{padding:'20px 24px'}}>
          {step==='confirm'&&(
            <div style={{display:'flex',flexDirection:'column',gap:16}}>
              <div style={{background:`${color}15`,border:`2px solid ${color}`,borderRadius:12,padding:18}}>
                <div style={{fontFamily:'var(--font-display)',fontSize:20,fontWeight:800,marginBottom:8}}>{plan.emoji} {plan.name}</div>
                <div style={{display:'flex',flexDirection:'column',gap:5,marginBottom:12}}>{feats.slice(0,4).map((f,i)=><div key={i} style={{display:'flex',gap:8,fontSize:13.5,color:'var(--text2)'}}><CheckCircle2 size={13} style={{color:'#10b981',flexShrink:0,marginTop:1}}/>{f}</div>)}</div>
              </div>
              <div style={{display:'flex',gap:8}}>
                {[['monthly','Monthly'],['annual','Annual (Save 20%)']].map(([v,l])=>(
                  <button key={v} onClick={()=>setBilling(v)} style={{flex:1,padding:10,borderRadius:8,border:`2px solid ${billing===v?color:'var(--border)'}`,background:billing===v?`${color}15`:'transparent',cursor:'pointer',fontWeight:600,fontSize:13}}>{l}</button>
                ))}
              </div>
              <div style={{fontSize:28,fontWeight:800,color:color,textAlign:'center'}}>₹{Number(amount).toLocaleString('en-IN')}<span style={{fontSize:13,color:'var(--text3)',fontWeight:400}}>/{billing==='annual'?'yr':'mo'}</span></div>
              <button onClick={initiate} disabled={loading} style={{width:'100%',padding:13,border:'none',borderRadius:9,background:color,color:'#fff',cursor:'pointer',fontWeight:700,fontSize:15}}>
                {loading?'Processing…':'Pay Now →'}
              </button>
            </div>
          )}
          {step==='payment'&&payInfo&&(
            <div style={{display:'flex',flexDirection:'column',gap:16}}>
              {payInfo.isPhonePe&&<div style={{background:'#22c55e15',border:'1px solid #22c55e',borderRadius:9,padding:12,fontSize:13.5}}>✅ PhonePe payment window opened. Complete payment, then enter your transaction ID below.</div>}
              <div style={{background:'var(--bg3)',borderRadius:9,padding:14}}>
                <div style={{fontSize:12,color:'var(--text3)',marginBottom:4}}>UPI ID / Account</div>
                <div style={{fontWeight:700,fontSize:16,color:'var(--text)'}}>{payInfo.upiId||payInfo.accountNumber||'—'}</div>
                <div style={{fontWeight:800,fontSize:22,color:color,marginTop:6}}>₹{Number(amount).toLocaleString('en-IN')}</div>
              </div>
              <div>
                <label style={{fontSize:12.5,color:'var(--text3)',display:'block',marginBottom:6}}>Transaction ID / UTR Number *</label>
                <input value={txnId} onChange={e=>setTxnId(e.target.value)} placeholder="e.g. 123456789012" style={{width:'100%',boxSizing:'border-box',padding:'10px 13px',border:'1px solid var(--border)',borderRadius:8,fontSize:14,color:'var(--text)',background:'var(--bg3)',outline:'none'}}/>
              </div>
              <button onClick={confirm} disabled={loading} style={{width:'100%',padding:13,border:'none',borderRadius:9,background:color,color:'#fff',cursor:'pointer',fontWeight:700,fontSize:15}}>
                {loading?'Confirming…':'Confirm Payment'}
              </button>
            </div>
          )}
          {step==='done'&&(
            <div style={{textAlign:'center',padding:'16px 0'}}>
              <div style={{fontSize:48,marginBottom:14}}>🎉</div>
              <div style={{fontWeight:700,fontSize:18,marginBottom:8}}>Payment submitted!</div>
              <div style={{color:'var(--text3)',fontSize:14,marginBottom:24}}>We'll verify and activate your plan within 24 hours.</div>
              <button onClick={()=>{onClose();navigate('/dashboard');}} style={{width:'100%',padding:13,border:'none',borderRadius:9,background:color,color:'#fff',cursor:'pointer',fontWeight:700,fontSize:15}}>Go to Dashboard →</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── LocationInput ──────────────────────────────────────────────────────────
function LocationInput({ value, onChange, onSearch, onFocusNavigate }) {
  const [q,setQ] = useState(value);
  const [sug,setSug] = useState([]);
  const [open,setOpen] = useState(false);
  const ref = useRef(null); const deb = useRef(null);
  useEffect(()=>{setQ(value);},[value]);
  useEffect(()=>{
    const h=e=>{if(ref.current&&!ref.current.contains(e.target))setOpen(false);};
    document.addEventListener('mousedown',h); return ()=>document.removeEventListener('mousedown',h);
  },[]);
  const suggest = async v => {
    if(!v||v.length<2){setSug([]);return;}
    try{const r=await fetch('/api/leads/location-search?q='+encodeURIComponent(v));const data=await r.json();const res=data.results||[];setSug(res);setOpen(res.length>0);}catch{setSug([]);}
  };
  return (
    <div ref={ref} style={{position:'relative',flex:1,minWidth:0}}>
      <span style={{position:'absolute',left:16,top:'50%',transform:'translateY(-50%)',color:'var(--text3)',pointerEvents:'none',zIndex:1}}><MapPin size={18}/></span>
      <input
        style={{width:'100%',boxSizing:'border-box',height:56,background:'var(--surface)',border:'2px solid var(--border)',borderRadius:'12px 0 0 12px',fontSize:15,color:'var(--text)',paddingLeft:46,paddingRight:16,outline:'none',fontFamily:'inherit',cursor:onFocusNavigate?'pointer':'text'}}
        placeholder="Enter city, area, or landmark…"
        value={q}
        onChange={e=>{if(onFocusNavigate)return;const v=e.target.value;setQ(v);onChange(v);clearTimeout(deb.current);deb.current=setTimeout(()=>suggest(v),260);}}
        onFocus={()=>{ if(onFocusNavigate){onFocusNavigate();return;} sug.length&&setOpen(true); }}
        onClick={()=>{ if(onFocusNavigate) onFocusNavigate(); }}
        onKeyDown={e=>{if(!onFocusNavigate&&e.key==='Enter'){onChange(q);onSearch();}}}
        readOnly={!!onFocusNavigate}
      />
      {q&&!onFocusNavigate&&<button style={{position:'absolute',right:12,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',color:'var(--text3)',padding:4,display:'flex'}} onClick={()=>{setQ('');onChange('');setSug([]);}}><X size={14}/></button>}
      {open&&sug.length>0&&!onFocusNavigate&&(
        <div style={{position:'absolute',top:'100%',left:0,right:0,zIndex:50,marginTop:4,background:'var(--surface)',border:'1px solid var(--border)',borderRadius:12,boxShadow:'0 12px 32px rgba(0,0,0,.14)',overflow:'hidden'}}>
          {sug.map((p,i)=>(
            <div key={p.place_id||i} style={{display:'flex',alignItems:'flex-start',gap:9,padding:'11px 16px',cursor:'pointer',borderBottom:'1px solid var(--border)',fontSize:13.5}}
              onMouseDown={()=>{setQ(p.main_text);onChange(p.main_text);setSug([]);setOpen(false);onSearch();}}
              onMouseEnter={e=>e.currentTarget.style.background='var(--bg3)'}
              onMouseLeave={e=>e.currentTarget.style.background=''}>
              <MapPin size={14} style={{color:'#e11d48',flexShrink:0,marginTop:2}}/>
              <div><div style={{color:'var(--text)',fontWeight:500}}>{p.main_text}</div>{p.secondary&&<div style={{fontSize:12,color:'var(--text3)',marginTop:1}}>{p.secondary}</div>}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ── LandingPage ────────────────────────────────────────────────────────────
export default function LandingPage() {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { user } = useAuth();
  const [location, setLocation] = useState('');
  const [plans, setPlans]           = useState([]);
  const [plansLoading, setPlansL]   = useState(true);
  const [annual, setAnnual]         = useState(false);
  const [buyPlan, setBuyPlan]       = useState(null);
  const [mobileOpen, setMobile]     = useState(false);

  useEffect(() => {
    api.get('/subscription/plans')
      .then(r => { setPlans((r.data.plans||[]).map(p=>({...p,features:typeof p.features==='string'?JSON.parse(p.features||'[]'):(p.features||[])}))); setPlansL(false); })
      .catch(() => setPlansL(false));
  }, []);

  const goSearch = () => {
    if (location.trim()) {
      // Pass location via sessionStorage so TrySearchPage can pick it up
      sessionStorage.setItem('lr_try_location', location.trim());
    }
    navigate('/try');
  };

  const handlePlanCTA = plan => {
    if (plan.price_monthly===null) { window.location.href='mailto:support@prepb.com?subject=Enterprise Plan Enquiry'; return; }
    if (plan.price_monthly===0) { navigate('/register'); return; }
    if (user) { setBuyPlan(plan); return; }
    navigate(`/register?plan=${plan.code}`);
  };

  const displayPrice = plan => {
    if (plan.price_monthly===null) return 'Custom';
    if (plan.price_monthly===0)    return '₹0';
    const p = annual&&plan.price_annual ? plan.price_annual : plan.price_monthly;
    return `₹${Number(p).toLocaleString('en-IN')}`;
  };

  return (
    <div style={{background:'var(--bg)',color:'var(--text)',fontFamily:'var(--font)',minHeight:'100vh'}}>
      <style>{`
        @keyframes fadeUp { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
        @media(max-width:768px){ .feat-grid{grid-template-columns:repeat(2,1fr)!important;} .plan-grid{grid-template-columns:1fr!important;overflow-x:auto;} }
        @media(max-width:480px){ .feat-grid{grid-template-columns:1fr!important;} }
        .lp-nav-links { display:flex; gap:4px; }
        @media(max-width:640px){ .lp-nav-links{display:none;} .hero-search{flex-direction:column!important;} .hero-search input{border-radius:12px!important;border-right:2px solid var(--border)!important;} .search-btn{border-radius:12px!important;height:52px!important;} }
      `}</style>

      {/* ── NAVBAR ── */}
      <nav style={{background:'var(--surface)',borderBottom:'1px solid var(--border)',position:'sticky',top:0,zIndex:50}}>
        <div style={{maxWidth:1200,margin:'0 auto',padding:'0 20px',height:58,display:'flex',alignItems:'center',gap:16}}>
          <div style={{fontFamily:'var(--font-display)',fontWeight:800,fontSize:20,cursor:'pointer',flexShrink:0}} onClick={()=>navigate('/')}>
            📡 Lead<span style={{color:'#e11d48'}}>Radar</span>
          </div>
          <div className="lp-nav-links" style={{marginLeft:16}}>
            {[['Features','#features'],['Pricing','#pricing']].map(([l,h])=>(
              <a key={l} href={h} style={{fontSize:14,fontWeight:500,color:'var(--text2)',textDecoration:'none',padding:'6px 12px',borderRadius:7}}>{l}</a>
            ))}
          </div>
          <div style={{marginLeft:'auto',display:'flex',gap:8,alignItems:'center'}}>
            <button onClick={toggleTheme} style={{background:'none',border:'1px solid var(--border)',borderRadius:8,padding:'6px 10px',cursor:'pointer',color:'var(--text2)'}}>
              {theme==='dark'?<Sun size={16}/>:<Moon size={16}/>}
            </button>
            {user ? (
              <button className="btn btn-primary btn-sm" onClick={()=>navigate('/dashboard')}>Dashboard →</button>
            ) : (
              <>
                <button className="btn btn-ghost btn-sm" onClick={()=>navigate('/login')}>Sign In</button>
                <button className="btn btn-primary btn-sm" onClick={()=>navigate('/register')}>Sign Up Free</button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* HERO — search bar IS the action                                   */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      <section style={{background:'linear-gradient(160deg,#fff1f2 0%,#fdf4ff 50%,#eff6ff 100%)',padding:'56px 20px 52px'}}>
        <div style={{maxWidth:760,margin:'0 auto',textAlign:'center',animation:'fadeUp .5s ease'}}>
          <div style={{display:'inline-flex',alignItems:'center',gap:7,background:'#e11d4818',border:'1px solid #e11d48',borderRadius:20,padding:'5px 16px',fontSize:12.5,color:'#e11d48',fontWeight:700,marginBottom:20}}>
            <Zap size={12}/> Lead Generation & Management (CRM) Platform
          </div>
          <h1 style={{fontFamily:'var(--font-display)',fontSize:'clamp(26px,5vw,52px)',fontWeight:900,lineHeight:1.1,marginBottom:16}}>
            Find business leads<br/>in <span style={{color:'#e11d48'}}>any city</span>, in seconds
          </h1>
          <p style={{fontSize:'clamp(14px,2vw,17px)',color:'var(--text3)',lineHeight:1.65,marginBottom:36,maxWidth:540,margin:'0 auto 36px'}}>
            Search real businesses with phone numbers, emails, and lead scores. No manual work. Just leads.
          </p>

          {/* ── THE SEARCH BAR ── */}
          <div className="hero-search" style={{display:'flex',maxWidth:660,margin:'0 auto 16px',boxShadow:'0 8px 32px rgba(225,29,72,.18)',borderRadius:14}}>
            <LocationInput value={location} onChange={setLocation} onSearch={goSearch} onFocusNavigate={()=>navigate('/try')}/>
            <button onClick={goSearch} className="search-btn"
              style={{display:'flex',alignItems:'center',gap:9,height:56,padding:'0 28px',borderRadius:'0 12px 12px 0',border:'none',background:'linear-gradient(135deg,#e11d48,#be123c)',color:'#fff',fontWeight:800,fontSize:16,cursor:'pointer',flexShrink:0,whiteSpace:'nowrap'}}>
              <Search size={18}/> Search Leads
            </button>
          </div>

          {/* Quick search chips */}
          <div style={{display:'flex',gap:8,flexWrap:'wrap',justifyContent:'center',marginBottom:28}}>
            {QUICK_SEARCHES.map(s=>(
              <button key={s.label} onClick={()=>{setLocation(s.loc);sessionStorage.setItem('lr_try_location',s.loc);navigate('/try');}}
                style={{display:'inline-flex',alignItems:'center',height:30,padding:'0 13px',borderRadius:20,border:'1px solid var(--border)',background:'var(--surface)',color:'var(--text2)',fontSize:12.5,cursor:'pointer',whiteSpace:'nowrap',fontWeight:500}}>
                {s.label}
              </button>
            ))}
          </div>

          {/* Trust signals */}
          <div style={{display:'flex',gap:20,justifyContent:'center',fontSize:13,color:'var(--text3)',flexWrap:'wrap'}}>
            {['Search free — no login needed','See real leads instantly','Sign up to save & reach out'].map(t=>(
              <span key={t} style={{display:'flex',alignItems:'center',gap:5}}>
                <CheckCircle2 size={13} style={{color:'#10b981'}}/>{t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ── STATS BAR ── */}
      <div style={{background:'var(--surface)',borderBottom:'1px solid var(--border)'}}>
        <div style={{maxWidth:900,margin:'0 auto',padding:'20px 24px',display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,textAlign:'center'}}>
          {[['10L+','Businesses indexed'],['80+','Business categories'],['<2s','Search speed'],['4.9★','User rating']].map(([n,l])=>(
            <div key={l}><div style={{fontFamily:'var(--font-display)',fontSize:'clamp(18px,3vw,26px)',fontWeight:800,color:'#e11d48'}}>{n}</div><div style={{fontSize:12,color:'var(--text3)',marginTop:2}}>{l}</div></div>
          ))}
        </div>
      </div>

      {/* ── HOW IT WORKS ── */}
      <section style={{maxWidth:1000,margin:'0 auto',padding:'56px 20px 48px',textAlign:'center'}}>
        <div style={{fontSize:12.5,fontWeight:700,textTransform:'uppercase',letterSpacing:1.5,color:'#e11d48',marginBottom:10}}>How it works</div>
        <h2 style={{fontFamily:'var(--font-display)',fontSize:'clamp(22px,3.5vw,34px)',fontWeight:800,marginBottom:40}}>3 steps to your next customer</h2>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))',gap:24}}>
          {[
            ['🔍','Search','Enter any city + business type. Get 20+ results instantly with contact details.'],
            ['💾','Save','Pick the best leads, save to your pipeline, assign to your sales team.'],
            ['📧','Reach Out','Send bulk emails or WhatsApp messages directly from the platform.'],
          ].map(([icon,title,desc],i)=>(
            <div key={i} style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:14,padding:'28px 22px',position:'relative'}}>
              <div style={{position:'absolute',top:-14,left:'50%',transform:'translateX(-50%)',width:28,height:28,borderRadius:'50%',background:'#e11d48',color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,fontWeight:800}}>{i+1}</div>
              <div style={{fontSize:36,marginBottom:12,marginTop:8}}>{icon}</div>
              <div style={{fontWeight:800,fontSize:16,marginBottom:8}}>{title}</div>
              <div style={{fontSize:13.5,color:'var(--text3)',lineHeight:1.55}}>{desc}</div>
            </div>
          ))}
        </div>
        <button onClick={()=>navigate('/try')} style={{marginTop:36,display:'inline-flex',alignItems:'center',gap:8,height:48,padding:'0 28px',borderRadius:12,border:'none',background:'#e11d48',color:'#fff',fontWeight:700,fontSize:15.5,cursor:'pointer'}}>
          <Search size={16}/> Try It Free Now <ArrowRight size={15}/>
        </button>
      </section>

      {/* ── FEATURES ── */}
      <section id="features" style={{background:'var(--bg3)',padding:'52px 20px'}}>
        <div style={{maxWidth:1100,margin:'0 auto',textAlign:'center'}}>
          <div style={{fontSize:12.5,fontWeight:700,textTransform:'uppercase',letterSpacing:1.5,color:'#e11d48',marginBottom:10}}>Features</div>
          <h2 style={{fontFamily:'var(--font-display)',fontSize:'clamp(22px,3.5vw,34px)',fontWeight:800,marginBottom:40}}>Everything you need to close more deals</h2>
          <div className="feat-grid" style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:16,textAlign:'left'}}>
            {FEATURES.map((f,i)=>(
              <div key={i} style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:12,padding:'20px 18px'}}>
                <div style={{fontSize:30,marginBottom:10}}>{f.icon}</div>
                <div style={{fontWeight:700,fontSize:15,marginBottom:6}}>{f.title}</div>
                <div style={{fontSize:13.5,color:'var(--text3)',lineHeight:1.55}}>{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section id="pricing" style={{maxWidth:1200,margin:'0 auto',padding:'56px 20px'}}>
        <div style={{textAlign:'center',marginBottom:36}}>
          <div style={{fontSize:12.5,fontWeight:700,textTransform:'uppercase',letterSpacing:1.5,color:'#e11d48',marginBottom:10}}>Pricing</div>
          <h2 style={{fontFamily:'var(--font-display)',fontSize:'clamp(22px,3.5vw,34px)',fontWeight:800,marginBottom:16}}>Simple, transparent pricing</h2>
          <div style={{display:'inline-flex',alignItems:'center',gap:8,background:'var(--bg3)',border:'1px solid var(--border)',borderRadius:10,padding:'6px 10px'}}>
            {[['monthly','Monthly'],['annual','Annual (–20%)']].map(([v,l])=>(
              <button key={v} onClick={()=>setAnnual(v==='annual')}
                style={{padding:'6px 14px',borderRadius:7,border:'none',cursor:'pointer',fontSize:13,fontWeight:600,background:annual===(v==='annual')?'var(--surface)':'transparent',color:annual===(v==='annual')?'var(--text)':'var(--text3)',boxShadow:annual===(v==='annual')?'0 1px 4px rgba(0,0,0,.1)':'none'}}>{l}</button>
            ))}
          </div>
        </div>
        {plansLoading ? (
          <div style={{textAlign:'center',padding:40}}><div className="spinner spinner-lg" style={{margin:'0 auto'}}/></div>
        ) : (
          <div className="plan-grid" style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:14,alignItems:'start'}}>
            {plans.map(plan=>{
              const color = PLAN_COLOR[plan.code]||'#6b7280';
              const isFree = plan.price_monthly===0;
              const isPop  = plan.code==='pro';
              return (
                <div key={plan.id} style={{background:'var(--surface)',border:`2px solid ${isPop?color:'var(--border)'}`,borderRadius:14,padding:'20px 16px',position:'relative'}}>
                  {isPop&&<div style={{position:'absolute',top:-12,left:'50%',transform:'translateX(-50%)',background:color,color:'#fff',fontSize:11,fontWeight:700,padding:'3px 12px',borderRadius:20,whiteSpace:'nowrap'}}>Most Popular</div>}
                  <div style={{fontSize:22,marginBottom:8}}>{plan.emoji||'📦'}</div>
                  <div style={{fontFamily:'var(--font-display)',fontWeight:800,fontSize:16,marginBottom:4}}>{plan.name}</div>
                  <div style={{fontSize:'clamp(22px,3vw,28px)',fontWeight:900,color,marginBottom:4}}>{displayPrice(plan)}<span style={{fontSize:12,color:'var(--text3)',fontWeight:400}}>/{annual&&plan.price_annual?'yr':'mo'}</span></div>
                  <div style={{fontSize:12,color:'var(--text3)',marginBottom:14}}>{plan.credits} searches/mo · {plan.max_members||1} member{plan.max_members>1?'s':''}</div>
                  <div style={{display:'flex',flexDirection:'column',gap:5,marginBottom:16}}>
                    {(plan.features||[]).slice(0,4).map((f,i)=>(
                      <div key={i} style={{display:'flex',gap:6,fontSize:12.5,color:'var(--text2)',alignItems:'flex-start'}}><CheckCircle2 size={12} style={{color:'#10b981',flexShrink:0,marginTop:2}}/>{f}</div>
                    ))}
                  </div>
                  <button onClick={()=>handlePlanCTA(plan)}
                    style={{width:'100%',padding:'9px 0',border:`2px solid ${color}`,borderRadius:9,cursor:'pointer',fontWeight:700,fontSize:13.5,background:isPop?color:'transparent',color:isPop?'#fff':color}}>
                    {isFree?'Start Free':plan.price_monthly===null?'Contact Us':'Upgrade'}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ── TESTIMONIALS ── */}
      <section style={{background:'var(--bg3)',padding:'48px 20px'}}>
        <div style={{maxWidth:900,margin:'0 auto',textAlign:'center'}}>
          <h2 style={{fontFamily:'var(--font-display)',fontSize:'clamp(20px,3vw,30px)',fontWeight:800,marginBottom:32}}>Loved by sales teams</h2>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(240px,1fr))',gap:16,textAlign:'left'}}>
            {[
              {name:'Priya Mehta',role:'Sales Head, FinServ Corp',text:'Cut prospecting time by 70%. We now generate 200+ qualified leads a week.',rating:5},
              {name:'Rohan Gupta',role:'Founder, GrowthHackers',text:'The lead score feature alone is worth it. Conversion doubled when we focus on 70+ score leads.',rating:5},
              {name:'Sneha Patil',role:'Marketing Manager, B2B Pulse',text:'Admin dashboard gives me full visibility into what my team is searching. Perfect control.',rating:5},
            ].map((t,i)=>(
              <div key={i} style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:12,padding:'18px 16px'}}>
                <div style={{display:'flex',gap:2,marginBottom:10}}>{Array(t.rating).fill(0).map((_,i)=><Star key={i} size={13} fill="#f59e0b" stroke="none"/>)}</div>
                <div style={{fontSize:13.5,color:'var(--text2)',lineHeight:1.6,marginBottom:12,fontStyle:'italic'}}>"{t.text}"</div>
                <div style={{fontWeight:700,fontSize:13}}>{t.name}</div>
                <div style={{fontSize:12,color:'var(--text3)'}}>{t.role}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── BOTTOM CTA ── */}
      <section style={{background:'linear-gradient(135deg,#e11d48,#be123c)',padding:'56px 24px',textAlign:'center',color:'#fff'}}>
        <h2 style={{fontFamily:'var(--font-display)',fontSize:'clamp(22px,4vw,38px)',fontWeight:800,marginBottom:14}}>Start finding leads right now</h2>
        <p style={{fontSize:16,opacity:.88,marginBottom:32,maxWidth:440,margin:'0 auto 32px',lineHeight:1.6}}>Search any city for free — no login needed. Sign up to save leads and start reaching out.</p>
        <div style={{display:'flex',gap:12,justifyContent:'center',flexWrap:'wrap'}}>
          <button onClick={()=>navigate('/try')} style={{display:'flex',alignItems:'center',gap:8,height:52,padding:'0 28px',borderRadius:12,border:'none',background:'#fff',color:'#e11d48',fontWeight:800,fontSize:16,cursor:'pointer'}}>
            <Search size={17}/> 🔍 Search Free — No Login
          </button>
          <button onClick={()=>navigate('/register')} style={{display:'flex',alignItems:'center',gap:8,height:52,padding:'0 28px',borderRadius:12,border:'2px solid rgba(255,255,255,.6)',background:'transparent',color:'#fff',fontWeight:700,fontSize:16,cursor:'pointer'}}>
            Create Free Account <ArrowRight size={16}/>
          </button>
        </div>
        <div style={{marginTop:20,fontSize:13,opacity:.7}}>Search free · Sign up to save · No credit card needed</div>
      </section>

      {/* ── FOOTER ── */}
      <footer style={{background:'var(--surface)',borderTop:'1px solid var(--border)',padding:'24px 20px',textAlign:'center'}}>
        <div style={{fontFamily:'var(--font-display)',fontWeight:700,fontSize:16,marginBottom:6}}>📡 Lead<span style={{color:'#e11d48'}}>Radar</span></div>
        <div style={{fontSize:12.5,color:'var(--text3)'}}>© {new Date().getFullYear()} LeadRadar · <a href="mailto:support@prepb.com" style={{color:'var(--text3)'}}>support@prepb.com</a></div>
      </footer>

      {buyPlan&&<PurchaseFlow plan={buyPlan} onClose={()=>setBuyPlan(null)}/>}
    </div>
  );
}